import React, { useEffect, useState } from "react";
import {Scrollable,Detail,Section,Header} from "coaz-ui";
import { useRef } from "react";
import client from '../client';
import {
	PiArrowLeftLight,
	PiArrowsLeftRightLight,
	PiBankLight,
	PiChartLineDownThin,
	PiChartLineUpThin,
	PiHandDeposit,
	PiMoneyLight,
	PiMoneyWavyThin,
	PiUserLight,
} from "react-icons/pi";
import LoadingIcons from "react-loading-icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const Account = () => {
	const [account, setAccount] = useState(null);
	const [holder, setHolder] = useState(null);
	const [transactions, setTransactions] = useState([]);
	const [buttons, setButtons] = useState([]);
	const [loading, setLoading] = useState(true);
	const holderRef = useRef(null);
	const { accountId } = useParams();
	const location = useLocation();
	const state = location.state;
	const transactionsRef = useRef(null);
	const padding = 16;
	const [contentSize, setContentSize] = useState({ width: 0, height: 0 });
	const {request,getCurrent,getByReference,errorMessage} = client;
	const path = useLocation().pathname;

	const navigate = useNavigate();

	let USDecimal = new Intl.NumberFormat("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});

    const onDeposit = (e) => {
        e.preventDefault();
        if(!holderRef.current || !holderRef.current.ref.id) return;
        navigate(`/_deposit/${holderRef.current.ref.id}`);
    }

	useEffect(() => {
		(async () => {
			setLoading(true);
			let current = await getCurrent();

			if (!current || !current.user) return;
			let acc = null;
			if (accountId) {
				let readAuth = current.authorities.find((auth) => {return auth.authority === "ACCOUNT:READ";});

				if (!readAuth) return;

				await request("GET", `accounts/${accountId}`)
					.then((response) => {
						if (response && response.ref) {
							acc = response;
						}
					})
					.catch((error) => {
						console.log(error);
					});
			} else {
				await request("GET", "accounts/by-holder", null, {
					holderReference: current.user.ref.value,
				})
					.then((response) => {
						if (response && response.ref) {
							acc = response;
                            setButtons([
                                {
                                    Icon:PiHandDeposit,
                                    name:'Deposit',
                                    handler:onDeposit
                                }
                            ]);
						}
					})
					.catch((error) => {
						console.log(error);
					});
			}

			if (!acc) return;

			if(acc.credits && acc.credits.length) {
				for(let tran of acc.credits) {
					tran.createdDate = tran.createdDate?new Date(tran.createdDate):new Date();
				}
			}

			await getByReference(acc.holderReference)
				.then((response) => {
					if (response && response.ref) {
						setHolder(response);
						holderRef.current = response;
					}
				})
				.catch(() => {});
			
			setAccount(acc);

			let trans = [...acc.debits, ...acc.credits];
			let sortedTrans = trans.sort((trans1, trans2) =>
				trans1.createdDate < trans2.createdDate
					? -1
					: trans1.createdDate > trans2.createdDate
						? 1
						: 0,
			);

			for(let tran of sortedTrans) {
				tran.createdDate = tran.createdDate?new Date(tran.createdDate):new Date();
			}
			
			setTransactions(sortedTrans);
			setLoading(false);
		})();
	}, []);

	if (account) {
		return (
			<div className="flex flex-col w-full h-full space-y-4 overflow-hidden">
				<Header Icon={PiBankLight} text="Account" buttons={buttons} />
				{loading ? (
					<div className="flex items-center justify-center w-full h-full">
						<LoadingIcons.SpinningCircles
							width={64}
							height={64}
							fill="rgb(1,175,240)"
						/>
					</div>
				) : (
					<Scrollable vertical={true}>
						<div className="flex flex-col w-full space-y-4">
							<Section name="Summary">
								<Detail
									Icon={PiBankLight}
									label="Name"
									value={account.name}
								/>
								<Detail
									Icon={PiUserLight}
									label="Holder"
									value={holder ? holder.name : "N/A"}
								/>
								<Detail
									Icon={PiChartLineUpThin}
									label="Debits"
									value={`K ${USDecimal.format(account.debit ? account.debit : "0")}`}
								/>
								<Detail
									Icon={PiChartLineDownThin}
									label="Credits"
									value={`K ${USDecimal.format(account.credit ? account.credit : "0")}`}
								/>
								<Detail
									Icon={PiMoneyLight}
									label="Balance"
									value={`K ${account.balance? account.balance < 0?"("+USDecimal.format(Math.abs(account.balance))+")":USDecimal.format(account.balance):USDecimal.format(0)}`}
								/>
								<Detail
									Icon={PiArrowsLeftRightLight}
									label="Transactions"
									value={transactions.length}
								/>
							</Section>
							<Section name="Transactions" grid={false}>
								<div className="relative flex flex-col w-full h-full">
									<div className="relative flex flex-col w-full space-y-2">
										<div className="flex flex-row w-full text-xs capitalize text-textFent">
											<p className="w-[25%] px-2">Date</p>
											<p className="w-[25%] px-2">
												Description
											</p>
											<p className="w-[25%] px-2">
												Debit
											</p>
											<p className="w-[25%] px-2">
												Credit
											</p>
										</div>
										{transactions &&
											transactions.map(
												(transaction, i) => (
													<div
														key={i}
														className="flex flex-row w-full"
													>
														<div className="w-[25%] px-2">
															<p className="w-full overflow-hidden text-xs break-words text-text">
																{transaction.createdDate && transaction.createdDate instanceof Date && transaction.createdDate.toLocaleString(
																	"default",
																	{
																		month: "long",
																	},
																) +
																	" " +
																	transaction.createdDate.getDate() +
																	", " +
																	transaction.createdDate.getFullYear() +
																	" " +
																	transaction.createdDate.toLocaleTimeString(
																		"en-US",
																	)}
															</p>
														</div>
														<div className="w-[25%] px-2">
															<p className="w-full overflow-hidden text-xs break-words text-text">
																{transaction.name &&
																transaction.name !=
																	""
																	? transaction.name
																	: transaction.transactionId}
															</p>
														</div>
														<div className="w-[25%] px-2">
															<p className="w-full overflow-hidden text-xs break-words text-text">
																{transaction.debitReference &&
																transaction
																	.debitReference ===
																	account.ref.value
																	? `K ${USDecimal.format(transaction.amount)}`
																	: ""}
															</p>
														</div>
														<div className="w-[25%] px-2">
															<p className="w-full overflow-hidden text-xs break-words text-text">
																{transaction.creditReference &&
																transaction
																	.creditReference ===
																	account.ref.value
																	? `K ${USDecimal.format(transaction.amount)}`
																	: ""}
															</p>
														</div>
													</div>
												),
											)}
										<div className="absolute top-0 bottom-0 left-0 right-0 flex flex-row">
											<div className="w-[25%] h-full" />
											<div className="w-[25%] h-full" />
											<div className="w-[25%] h-full" />
											<div className="w-[25%] h-full" />
										</div>
									</div>
								</div>
							</Section>
						</div>
					</Scrollable>
				)}
			</div>
			/*<div className="flex flex-col w-full h-full shrink-0 p-8 space-y-4 tracking-wider text-[rgb(68,71,70)] font-helveticaNeueMedium">
			<div className="flex flex-row items-center justify-between">
				<div className="flex flex-row items-center space-x-2">
					<button
						onClick={(e) => {
							const index = path.lastIndexOf("/_");
							const result =
								index !== -1 ? path.slice(0, index) : path;
							navigate(result);
						}}
						className="flex w-10 h-10 items-center justify-center rounded-full text-[rgb(0,175,240)] hover:bg-[rgba(0,0,0,.04)]"
					>
						<PiArrowLeftLight size={20} />
					</button>
					<p className="text-sm tracking-wider text-[rgb(68,71,70)] font-helveticaNeueMedium shrink-0 uppercase">
						{account && account.holder
							? account.holder.name
							: account && account.name
								? account.name
								: ""}
					</p>
				</div>
			</div>
			<div className="flex flex-wrap w-full h-fit justify-between xl:px-[25%] overflow-hidden bg-white shrink-0 border border-[rgba(0,175,240,.2)] rounded-md">
				<AccountAttribute
					Icon={PiChartLineUpThin}
					label={"Debits"}
					value={debits}
				/>
				<AccountAttribute
					Icon={PiChartLineDownThin}
					label={"Credits"}
					value={credits}
				/>
				<AccountAttribute
					Icon={PiMoneyWavyThin}
					label={"Balance"}
					value={balance}
				/>
			</div>
			<div
				ref={transactionsRef}
				style={{ padding: padding + "px" }}
				className="relative flex flex-col w-full h-full bg-white border border-[rgba(0,175,240,.2)] overflow-hidden rounded-md"
			>
				<Scrollable vertical={true} horizontal={true}>
					<div
						style={{
							width: contentSize.width + "px",
							height: contentSize.height + "px",
						}}
						className="relative flex flex-col min-w-[680px]"
					>
						<div className="flex flex-row w-full text-sm text-[rgb(68,71,70)] font-helveticaNeueMedium capitalize">
							<p className="w-[25%] px-2">Date</p>
							<p className="w-[25%] px-2">Description</p>
							<p className="w-[25%] px-2">Debit</p>
							<p className="w-[25%] px-2">Credit</p>
						</div>
						{transactions &&
							transactions.map((transaction, i) => (
								<div key={i} className="flex flex-row w-full">
									<div className="w-[25%] p-2">
										<p className="w-full p-2 text-xs text-[rgb(68,71,70)] overflow-hidden break-words font-helveticaNeueRegular">
											{transaction.createdOn.toLocaleString(
												"default",
												{ month: "long" },
											) +
												" " +
												transaction.createdOn.getDate() +
												", " +
												transaction.createdOn.getFullYear() +
												" " +
												transaction.createdOn.toLocaleTimeString(
													"en-US",
												)}
										</p>
									</div>
									<div className="w-[25%] p-2">
										<p className="w-full p-2 text-xs text-[rgb(68,71,70)] overflow-hidden break-words font-helveticaNeueRegular">
											{transaction.description &&
											transaction.description != ""
												? transaction.description
												: transaction.transactionId}
										</p>
									</div>
									<div className="w-[25%] p-2">
										<p className="w-full p-2 text-xs text-[rgb(68,71,70)] overflow-hidden break-words font-helveticaNeueRegular">
											{transaction.debit &&
											transaction.debit.id === account.id
												? transaction.amount
												: ""}
										</p>
									</div>
									<div className="w-[25%] p-2">
										<p className="w-full p-2 text-xs text-[rgb(68,71,70)] overflow-hidden break-words font-helveticaNeueRegular">
											{transaction.credit &&
											transaction.credit.id === account.id
												? transaction.amount
												: ""}
										</p>
									</div>
								</div>
							))}
						<div className="absolute top-0 bottom-0 left-0 right-0 flex flex-row">
							<div className="w-[25%] h-full border-r border-[rgba(0,175,240,.2)]" />
							<div className="w-[25%] h-full border-r border-[rgba(0,175,240,.2)]" />
							<div className="w-[25%] h-full border-r border-[rgba(0,175,240,.2)]" />
							<div className="w-[25%] h-full" />
						</div>
					</div>
				</Scrollable>
			</div>
		</div>*/
		);
	} else {
		<></>;
	}
};

export default Account;

const AccountAttribute = ({ Icon, label, value }) => {
	return (
		<div className="flex flex-col items-center p-2 sm:flex-row w-fit h-fit sm:space-x-4 shrink-0">
			<div className="flex w-10 h-10 items-center justify-center text-[rgb(0,175,240)] bg-[rgba(0,175,240,.2)] shrink-0 rounded-md">
				<Icon size={24} />
			</div>
			<div className="flex flex-col items-center justify-center sm:justify-normal">
				<p className="text-xs text-[rgb(145,145,145)] font-helveticaNeueRegular">
					{label}
				</p>
				<p className="flex w-full justify-center sm:justify-normal font-helveticaNeueMedium text-[rgb(0,175,240)] uppercase">
					{value}
				</p>
			</div>
		</div>
	);
};
